/* eslint-disable i18next/no-literal-string */
import React, { useEffect, useRef, useState } from 'react';
import feature1Img from 'assets/images/general-landing/features/1.png';
import feature2Img from 'assets/images/general-landing/features/2.png';
import feature3_1Img from 'assets/images/general-landing/features/3-1.png';
import feature3_2Img from 'assets/images/general-landing/features/3-2.png';
import feature4_1Img from 'assets/images/general-landing/features/4-1.png';
import feature4_2Img from 'assets/images/general-landing/features/4-2.png';
import feature5_1Img from 'assets/images/general-landing/features/5-1.jpg';
import feature5_2Img from 'assets/images/general-landing/features/5-2.jpg';
import feature6Img from 'assets/images/general-landing/features/6.png';
import ReactCompareImage from 'react-compare-image';
import styles from './Features.module.scss';

function FeatureCard({
  number,
  title,
  description,
  children,
  isStacked,
  stackPosition,
  isOffScreen,
  offScreenDirection,
}) {
  const getStackStyle = () => {
    if (isOffScreen) {
      // Move card off screen based on direction
      const translateX = offScreenDirection === 'left' ? '-150%' : '150%';
      return {
        transform: `translate(${translateX}, 0)`,
        opacity: 0,
      };
    }

    if (!isStacked) return {};

    const translateX = stackPosition * 20; // 20px offset per card
    const zIndex = 10 + stackPosition; // Higher z-index for cards on top

    return {
      transform: `translateX(${translateX}px)`,
      zIndex,
    };
  };

  return (
    <div
      className={`${styles.card} ${isStacked ? styles.stacked : ''}`}
      style={getStackStyle()}
    >
      <div className={styles.text}>
        <div className={styles.number}>
          {number}
        </div>
        <div className={styles.titleText}>
          {title}
        </div>
        <div className={styles.description}>
          {description}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function Features() {
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [stackedCards, setStackedCards] = useState(new Set([0])); // Initialize with card 01
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  const featuresData = [
    {
      number: '01',
      title: 'Virtual Staging App',
      description: 'Unleash your inner designer with DIY virtual staging. Our platform offers endless customization options, so you can create the perfect ambiance for any property.',
      content: <img src={feature1Img} alt="01" className={styles.image} />,
    },
    {
      number: '02',
      title: '3D Scanner App',
      description: 'Stage faster with our 3D scanner app. Generate accurate floor plans and room layouts in minutes, streamlining your virtual staging workflow and saving you time.',
      content: <img src={feature2Img} alt="02" className={styles.image} />,
    },
    {
      number: '03',
      title: 'Photo Editing AI App',
      description: 'Perfect your photos effortlessly with our AI-powered photo editing app. Retouch, enhance, and transform your images with just a few taps.',
      content: (
        <div className={styles.compare}>
          <ReactCompareImage
            leftImage={feature3_1Img}
            rightImage={feature3_2Img}
          />
        </div>
      ),
    },
    {
      number: '04',
      title: '2D & 3D Floor Plan App',
      description: 'Our ecosystem seamlessly integrates virtual staging, 2D & 3D floor plans, and more, empowering you to create captivating presentations.',
      content: (
        <div className={styles.compare}>
          <ReactCompareImage
            leftImage={feature4_1Img}
            rightImage={feature4_2Img}
          />
        </div>
      ),
    },
    {
      number: '05',
      title: 'Interior Rendering App',
      description: 'Experiment with different styles and layouts using DIY interior rendering. Visualize your dream spaces and bring your creative vision to life.',
      content: (
        <div className={styles.compare}>
          <ReactCompareImage
            leftImage={feature5_1Img}
            rightImage={feature5_2Img}
          />
        </div>
      ),
    },
    {
      number: '06',
      title: '3D Virtual Tour App',
      description: 'Offer buyers an immersive experience with DIY 3D virtual tours. Let them explore every corner of your properties as if they were truly there.',
      content: (
        <a href="https://diy.betates.com/published-tours/test%20360-1737686830880" target="_blank" rel="noreferrer">
          <img src={feature6Img} alt="06" className={styles.image} />
        </a>
      ),
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      // Check if we're on mobile (screen width <= 1000px)
      const isMobile = window.innerWidth <= 1000;

      if (isMobile) {
        // On mobile, show all cards vertically (no stacking)
        const allCards = new Set();
        for (let i = 0; i < featuresData.length; i++) {
          allCards.add(i);
        }
        setStackedCards(allCards);
        setCurrentCardIndex(0);
        return;
      }

      const wrapper = wrapperRef.current;
      const viewportHeight = window.innerHeight;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      const wrapperHeight = wrapper.getBoundingClientRect().height;

      // Calculate progress from 0 to 1
      // eslint-disable-next-line no-mixed-operators
      const progress = Math.round((viewportHeight - wrapperTop) / wrapperHeight * 100) / 100;

      // Determine which cards should be stacked based on progress
      const totalCards = featuresData.length;
      const cardsToStack = Math.max(1, Math.floor(progress * totalCards)); // Ensure at least 1 card is stacked
      const newStackedCards = new Set();

      // Add cards in order (first card will be at the bottom of the stack)
      for (let i = 0; i < cardsToStack; i++) {
        newStackedCards.add(i);
      }

      // Update current card index based on progress
      const newCurrentIndex = Math.min(Math.floor(progress * totalCards), totalCards - 1);
      setCurrentCardIndex(newCurrentIndex);

      setStackedCards(newStackedCards);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll); // Add resize listener for mobile detection
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [featuresData.length]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.container} ref={containerRef}>
        <div className={styles.title}>
          <div className={styles.tag}>CORE</div>
          <div className={styles.titleText}>
            Features
          </div>
        </div>
        <div className={styles.subtitle}>
          Explore DIY Betates Powerful Staging Platform
        </div>
        <div className={styles.features}>
          <div className={styles.cardsContainer}>
            {featuresData.map((feature, index) => (
              <FeatureCard
                key={feature.number}
                number={feature.number}
                title={feature.title}
                description={feature.description}
                isStacked={stackedCards.has(index)}
                stackPosition={Array.from(stackedCards).indexOf(index)}
                isOffScreen={!stackedCards.has(index)}
                offScreenDirection={index < currentCardIndex ? 'left' : 'right'}
              >
                {feature.content}
              </FeatureCard>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
